import { clsx } from 'clsx';
import { renderToString } from 'react-dom/server';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, MarkerF, OverlayViewF } from '@react-google-maps/api';

import { Loader } from '@components';
import { CreditFarm } from '@types';
import { defaultMapPosition, IconPoint } from './FieldReport';
import cls from './FieldReport.module.css';


const google_api_key = process.env.REACT_APP_GOOGLE_MAPS_API as string;

interface Props {
  farm: CreditFarm;
  selected: number | null;
  setSelected:Dispatch<SetStateAction<number | null>>;
  data: google.maps.LatLngLiteral[];
  setData:Dispatch<SetStateAction<google.maps.LatLngLiteral[]>>;
  editable?: boolean;
}

export const FieldReportPolygonMap = (
  { farm, data, setData, selected, setSelected, editable }: Props,
) => {
  const { isLoaded } = useJsApiLoader({
    id: 'credit-farm-script',
    googleMapsApiKey: google_api_key,
  });
  const [mapReady, setMapReady] = useState(false);
  const [position, setPosition] = useState<google.maps.LatLngLiteral>(
    !farm.polygon?.length ? defaultMapPosition : farm.polygon[0],
  );

  const icons = useMemo(() => ({
    first: renderToString(<IconPoint />),
    middle: renderToString(<IconPoint color='#FFC82D' />),
    last: renderToString(<IconPoint color='#FF0000' />),
    single: renderToString(<IconPoint color='#FD8A32' />),
  }), []);

  const onLoad = (nextMap: google.maps.Map) => {
    nextMap.setCenter(new google.maps.LatLng(position.lat, position.lng));
    nextMap.setOptions({ draggableCursor: 'se-resize' });
    google.maps.event.addListenerOnce(
      nextMap,
      'idle',
      () => {
        setMapReady(true);
      },
    );
  };

  const handleMapClick = (e: any) => {
    if (!editable) return;
    const lat = +e.latLng.lat().toFixed(5);
    const lng = +e.latLng.lng().toFixed(5);

    setData((prev) => [
      ...prev,
      { lat, lng },
    ]);
    !data.length && setPosition({ lat, lng });
  };

  const onTooltipClose = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setSelected(null);
  };

  if (!isLoaded) {
    return <Loader />;
  }

  return (
    <div className={clsx(cls.map)}>
      <GoogleMap
        onClick={handleMapClick}
        mapContainerStyle={{
          width: '100%',
          minHeight: '320px',
          userSelect: 'none',
          border: '1px solid #AAA',
          borderRadius: '8px 8px 8px 8px',
        }}
        zoom={8}
        center={position}
        onLoad={onLoad}
        options={{
          minZoom: 6,
          controlSize: 20,
          mapTypeId: 'satellite',
        }}
      >
        {data.map((point, index) => {
          const icon = !index ? icons.first
            : data.length > 2 && index + 1 === data.length ? icons.last : icons.middle;
          return (
            <React.Fragment key={index}>
              {selected === index && (
                <OverlayViewF
                  position={point}
                  mapPaneName="floatPane"
                >
                  <div className={cls.mapTooltip}>
                    <button
                      className={cls.mapTooltipClose}
                      onClick={onTooltipClose}>+</button>
                    <div className={cls.mapTooltipText}>{point.lat}, {point.lng}</div>
                  </div>
                </OverlayViewF>
              )}
              <MarkerF
                key={index}
                position={point}
                onClick={() => setSelected(selected === index ? null : index)}
                onRightClick={() => {
                  if (!editable) return;
                  setData((prev) => prev.filter((_, pointIndex) => pointIndex !== index));
                  setSelected(null);
                }}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(icon)}`,
                  scaledSize: new google.maps.Size(20, 20),
                }}
              />
            </React.Fragment>
          );})}

        {farm.latitude && farm.longitude ? (
          <MarkerF
            position={{
              lat: +farm.latitude,
              lng: +farm.longitude,
            }}
            icon={{
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(icons.single)}`,
              scaledSize: new google.maps.Size(20, 20),
            }}
          />
        ) : null}

        {mapReady ? <Polygon
          paths={[...data]}
          key={data.length}
          options={{
            fillColor: '#0088ff',
            fillOpacity: 0.7,
            strokeColor: '#0088ff',
            strokeWeight: 2,
          }}
        /> : null}
      </GoogleMap>
    </div>
  );
};
